/**
 * Client-side helpers for Impact Checking nonprofit donation selection.
 */

import type { Product, ProductFeatures } from './product-types'
import type { CartItem } from '@/components/account-cart-provider'

export type ImpactDonationStatus = 'selected' | 'skipped'

export type ImpactDonationAccountSelection = {
  accountId: string
  accountTitle?: string
  targetId: string
  targetName?: string
  coreIdentifier?: string
  selectedAt: string
}

export type ImpactDonationSkipped = {
  status: 'skipped'
  selectedAt: string
}

export type ImpactDonationSelections = {
  selections: ImpactDonationAccountSelection[]
}

export type ImpactDonationChoice = ImpactDonationSelections | ImpactDonationSkipped

export type DonationSelectionAccount = {
  accountId: string
  description: string
  instanceNumber: number
}

/** Sentinel account id for application-wide donation selection. */
export const APPLICATION_DONATION_ACCOUNT_ID = '__application__'

export type DonationFundingRow = {
  accountId: string
  title?: string
  type?: string
  productFeatures?: ProductFeatures | null
}

export type DonationEligibilityInput = {
  cart?: CartItem[]
  fundingRows?: DonationFundingRow[]
  products: Product[]
  persistedAccounts?: DonationSelectionAccount[]
  expressProductId?: string
}

export type ResumeImpactDonation = ImpactDonationChoice

export const DONATION_RETURN_TO_SESSION_KEY = 'cao_donation_return_to'

export type DonationReturnPath = '/apply' | '/apply/express'

export function productHasDonationSelection(features?: ProductFeatures | null): boolean {
  return features?.impactCheckingDonationSelection === true
}

export function productRowHasDonationSelection(
  accountId: string,
  productFeatures: ProductFeatures | null | undefined,
  products: Product[],
): boolean {
  if (productHasDonationSelection(productFeatures)) {
    return true
  }
  const baseId = resolveBaseProductId(accountId)
  const catalogProduct = products.find((product) => product.id === baseId)
  if (productHasDonationSelection(catalogProduct?.features)) {
    return true
  }
  if (
    productFeatures?.impactCheckingDonationSelection === false
    || catalogProduct?.features?.impactCheckingDonationSelection === false
  ) {
    return false
  }
  // short-term fallback for product check failing. near future only one product 
  // will support this functionality
  return baseId === 'impact-checking'
}

/** Whether a manually added funding account should show donation selection UI. */
export function fundingAddProductRequiresDonationSelection(
  productId: string,
  products: Product[],
  productFeatures?: ProductFeatures | null,
): boolean {
  return productRowHasDonationSelection(productId, productFeatures, products)
}

/** Resolve catalog product id from cart/funding account ids (manual-*, *-2). */
export function resolveBaseProductId(accountId: string): string {
  const trimmed = accountId.trim()
  if (!trimmed) {
    return ''
  }
  const withoutManual = trimmed.startsWith('manual-')
    ? trimmed.slice('manual-'.length).replace(/-\d+$/, '')
    : trimmed.replace(/-\d+$/, '')
  return withoutManual
}

export function resolveDonationEligibleProductIds(input: DonationEligibilityInput): string[] {
  const productIds = new Set<string>()
  const { cart = [], fundingRows = [], products, expressProductId } = input

  if (expressProductId && productRowHasDonationSelection(expressProductId, undefined, products)) {
    productIds.add(resolveBaseProductId(expressProductId))
  }

  for (const item of cart) {
    if (productRowHasDonationSelection(item.accountType.id, item.accountType.productFeatures, products)) {
      productIds.add(resolveBaseProductId(item.accountType.id))
    }
  }

  for (const row of fundingRows) {
    if (productRowHasDonationSelection(row.accountId, row.productFeatures, products)) {
      productIds.add(resolveBaseProductId(row.accountId))
    }
  }

  const persisted = normalizeDonationSelectionAccounts(input.persistedAccounts ?? [])
  for (const account of persisted) {
    if (productRowHasDonationSelection(account.accountId, undefined, products)) {
      productIds.add(resolveBaseProductId(account.accountId))
    }
  }

  return [...productIds].filter(Boolean)
}

export function applicationRequiresDonationSelection(input: DonationEligibilityInput): boolean {
  return resolveDonationEligibleProductIds(input).length > 0
}

export function normalizeDonationSelectionAccounts(value: unknown): DonationSelectionAccount[] {
  if (!Array.isArray(value)) {
    return []
  }

  const accounts: DonationSelectionAccount[] = []
  for (const entry of value) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      continue
    }
    const record = entry as Record<string, unknown>
    const accountId = typeof record.accountId === 'string' ? record.accountId.trim() : ''
    const description = typeof record.description === 'string' ? record.description.trim() : ''
    const instanceNumber = typeof record.instanceNumber === 'number' && Number.isFinite(record.instanceNumber)
      ? record.instanceNumber
      : accounts.length + 1
    if (!accountId || !description) {
      continue
    }
    accounts.push({ accountId, description, instanceNumber })
  }

  return accounts.map((account, index) => ({
    ...account,
    instanceNumber: index + 1,
  }))
}

/** @deprecated Use applicationRequiresDonationSelection — kept for resume meta compatibility. */
export function getDonationSelectionAccounts(
  cart: CartItem[],
  products: Product[]
): DonationSelectionAccount[] {
  const eligibleIds = resolveDonationEligibleProductIds({ cart, products })
  return eligibleIds.map((productId, index) => {
    const product = products.find((entry) => entry.id === productId)
    return {
      accountId: productId,
      description: product?.accountDescription || product?.productHeadline || productId,
      instanceNumber: index + 1,
    }
  })
}

export function resolveDonationSelectionAccounts(
  cart: CartItem[],
  products: Product[],
  persistedAccounts: DonationSelectionAccount[] = []
): DonationSelectionAccount[] {
  const fromEligibility = getDonationSelectionAccounts(cart, products)
  if (fromEligibility.length > 0) {
    return fromEligibility
  }
  return normalizeDonationSelectionAccounts(persistedAccounts)
}

export function cartRequiresDonationSelection(
  cart: CartItem[],
  products: Product[]
): boolean {
  return applicationRequiresDonationSelection({ cart, products })
}

export function requiresDonationSelectionForApplication(
  cart: CartItem[],
  products: Product[],
  persistedAccounts: DonationSelectionAccount[] = [],
  fundingRows: DonationFundingRow[] = [],
  expressProductId?: string
): boolean {
  return applicationRequiresDonationSelection({
    cart,
    fundingRows,
    products,
    persistedAccounts,
    expressProductId,
  })
}

/** Express donation step eligibility from live funding accounts (not stale resume metadata). */
export function resolveExpressRequiresDonationSelection(input: {
  fundingRows: DonationFundingRow[]
  cart?: CartItem[]
  products: Product[]
  resumeSummary?: DonationResumeSummary | null
}): boolean {
  const { fundingRows, cart = [], products, resumeSummary } = input

  if (fundingRows.length > 0) {
    return applicationRequiresDonationSelection({
      fundingRows,
      products,
      cart: [],
      persistedAccounts: [],
    })
  }

  if (cart.length > 0) {
    return applicationRequiresDonationSelection({
      cart,
      products,
      fundingRows: [],
      persistedAccounts: [],
    })
  }

  const resumeFundingProducts = resumeSummary?.funding?.products ?? []
  if (resumeFundingProducts.length > 0) {
    return applicationRequiresDonationSelection({
      fundingRows: resumeFundingProducts.map((product) => ({
        accountId: product.accountId ?? '',
      })),
      products,
      cart: [],
      persistedAccounts: [],
    })
  }

  return false
}

export type DonationResumeSummary = {
  requiresDonationSelection?: boolean
  donationSelectionAccounts?: unknown
  impactDonation?: unknown
  funding?: {
    products: Array<{ accountId?: string }>
  }
}

/** Infer donation requirement from a non-PII resume summary (Apply + Express). */
export function inferRequiresDonationFromResumeSummary(
  summary: DonationResumeSummary,
  products: Product[] = []
): boolean {
  if (summary.requiresDonationSelection === true) {
    return true
  }
  if (normalizeDonationSelectionAccounts(summary.donationSelectionAccounts).length > 0) {
    return true
  }
  if (hasApplicationDonationSelection(readResumeImpactDonation(summary.impactDonation))) {
    return true
  }
  const fundingProducts = summary.funding?.products ?? []
  if (fundingProducts.length === 0) {
    return false
  }
  if (products.length > 0) {
    return applicationRequiresDonationSelection({
      fundingRows: fundingProducts.map((product) => ({
        accountId: product.accountId ?? '',
      })),
      products,
    })
  }
  return fundingProducts.some((product) => {
    const baseId = resolveBaseProductId(product.accountId ?? '')
    return baseId === 'impact-checking'
  })
}

export function cartItemRequiresDonationSelection(cart: CartItem[]): boolean {
  return cart.some((item) => productHasDonationSelection(item.accountType.productFeatures))
}

export function expressProductRequiresDonationSelection(
  productId: string,
  products: Product[]
): boolean {
  return applicationRequiresDonationSelection({ products, expressProductId: productId })
}

export function getExpressDonationSelectionAccounts(
  productId: string,
  products: Product[]
): DonationSelectionAccount[] {
  if (!expressProductRequiresDonationSelection(productId, products)) {
    return []
  }

  const product = products.find((entry) => entry.id === productId)
  return [{
    accountId: productId,
    description: product?.accountDescription || product?.productHeadline || productId,
    instanceNumber: 1,
  }]
}

function isSkippedChoice(value: ImpactDonationChoice | null): value is ImpactDonationSkipped {
  return Boolean(value && 'status' in value && value.status === 'skipped')
}

function isSelectionsChoice(value: ImpactDonationChoice | null): value is ImpactDonationSelections {
  return Boolean(value && 'selections' in value && Array.isArray(value.selections))
}

function readCoreIdentifier(record: Record<string, unknown>): string {
  return typeof record.coreIdentifier === 'string' ? record.coreIdentifier.trim() : ''
}

function normalizeAccountSelectionEntry(value: unknown): ImpactDonationAccountSelection | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  const selection = value as Record<string, unknown>
  const accountId = typeof selection.accountId === 'string' ? selection.accountId.trim() : APPLICATION_DONATION_ACCOUNT_ID
  const targetId = typeof selection.targetId === 'string' ? selection.targetId.trim() : ''
  if (!targetId) {
    return null
  }
  return {
    accountId,
    ...(typeof selection.accountTitle === 'string' && selection.accountTitle.trim()
      ? { accountTitle: selection.accountTitle.trim() }
      : {}),
    targetId,
    ...(typeof selection.targetName === 'string' && selection.targetName.trim()
      ? { targetName: selection.targetName.trim() }
      : {}),
    ...(readCoreIdentifier(selection)
      ? { coreIdentifier: readCoreIdentifier(selection) }
      : {}),
    selectedAt: typeof selection.selectedAt === 'string' && selection.selectedAt.trim()
      ? selection.selectedAt.trim()
      : new Date().toISOString(),
  }
}

export function upgradeLegacyImpactDonationChoice(
  value: unknown,
  fallbackAccountIds: string[] = []
): ImpactDonationChoice | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const record = value as Record<string, unknown>

  if (Array.isArray(record.selections) && record.selections.length > 0) {
    const selections = record.selections
      .map((entry) => normalizeAccountSelectionEntry(entry))
      .filter((entry): entry is ImpactDonationAccountSelection => entry !== null)
    if (selections.length === 0) {
      return null
    }
    const canonical = selections[0]
    return {
      selections: [{
        ...canonical,
        accountId: APPLICATION_DONATION_ACCOUNT_ID,
      }],
    }
  }

  if (record.status === 'skipped') {
    return {
      status: 'skipped',
      selectedAt: typeof record.selectedAt === 'string' && record.selectedAt.trim()
        ? record.selectedAt.trim()
        : new Date().toISOString(),
    }
  }

  if (record.status === 'selected' && typeof record.targetId === 'string' && record.targetId.trim()) {
    const targetId = record.targetId.trim()
    const selectedAt = typeof record.selectedAt === 'string' && record.selectedAt.trim()
      ? record.selectedAt.trim()
      : new Date().toISOString()

    return {
      selections: [{
        accountId: APPLICATION_DONATION_ACCOUNT_ID,
        targetId,
        ...(typeof record.targetName === 'string' && record.targetName.trim()
          ? { targetName: record.targetName.trim() }
          : {}),
        ...(readCoreIdentifier(record)
          ? { coreIdentifier: readCoreIdentifier(record) }
          : {}),
        selectedAt,
      }],
    }
  }

  if (typeof record.targetId === 'string' && record.targetId.trim()) {
    const normalized = normalizeAccountSelectionEntry(record)
    return normalized ? { selections: [normalized] } : null
  }

  void fallbackAccountIds
  return null
}

export function normalizeImpactDonationChoice(
  value: unknown,
  fallbackAccountIds: string[] = []
): ImpactDonationChoice | null {
  return upgradeLegacyImpactDonationChoice(value, fallbackAccountIds)
}

export function readImpactDonationSelections(
  impactDonation: ImpactDonationChoice | null | undefined,
  _fallbackAccountIds: string[] = []
): ImpactDonationAccountSelection[] {
  if (!impactDonation) {
    return []
  }
  if (isSkippedChoice(impactDonation)) {
    return []
  }
  if (isSelectionsChoice(impactDonation)) {
    return impactDonation.selections
  }
  return []
}

export function getApplicationDonationSelection(
  impactDonation: ImpactDonationChoice | null | undefined,
  fallbackAccountIds: string[] = []
): ImpactDonationAccountSelection | null {
  const upgraded = impactDonation
    ? upgradeLegacyImpactDonationChoice(impactDonation, fallbackAccountIds)
    : null
  if (!upgraded || isSkippedChoice(upgraded)) {
    return null
  }
  return upgraded.selections[0] ?? null
}

export function hasApplicationDonationSelection(
  impactDonation: ImpactDonationChoice | null | undefined,
  fallbackAccountIds: string[] = []
): boolean {
  return Boolean(getApplicationDonationSelection(impactDonation, fallbackAccountIds)?.targetId)
}

export function createApplicationDonationSelection(selection: {
  targetId: string
  targetName?: string
  coreIdentifier?: string
}): ImpactDonationSelections {
  return {
    selections: [{
      accountId: APPLICATION_DONATION_ACCOUNT_ID,
      targetId: selection.targetId,
      ...(selection.targetName ? { targetName: selection.targetName } : {}),
      ...(selection.coreIdentifier ? { coreIdentifier: selection.coreIdentifier } : {}),
      selectedAt: new Date().toISOString(),
    }],
  }
}

export function mergeApplicationDonationSelection(
  _impactDonation: ImpactDonationChoice | null | undefined,
  selection: { targetId: string; targetName?: string; coreIdentifier?: string }
): ImpactDonationSelections {
  return createApplicationDonationSelection(selection)
}

/** @deprecated Use getApplicationDonationSelection — per-account lookup retained for legacy data. */
export function getSelectionForAccount(
  impactDonation: ImpactDonationChoice | null | undefined,
  accountId: string,
  fallbackAccountIds: string[] = []
): ImpactDonationAccountSelection | null {
  const upgraded = impactDonation
    ? upgradeLegacyImpactDonationChoice(impactDonation, fallbackAccountIds)
    : null
  if (!upgraded || isSkippedChoice(upgraded)) {
    return null
  }
  const perAccount = upgraded.selections.find((selection) => selection.accountId === accountId)
  if (perAccount) {
    return perAccount
  }
  return getApplicationDonationSelection(upgraded, fallbackAccountIds)
}

export function isImpactDonationComplete(
  requiresDonation: boolean,
  impactDonation: ImpactDonationChoice | null | undefined
): boolean {
  if (!requiresDonation) {
    return true
  }
  return hasApplicationDonationSelection(impactDonation)
}

/** @deprecated Use mergeApplicationDonationSelection. */
export function mergeAccountSelection(
  impactDonation: ImpactDonationChoice | null | undefined,
  _account: DonationSelectionAccount,
  selection: { targetId: string; targetName?: string },
  _totalCount: number,
  _accounts: DonationSelectionAccount[] = []
): ImpactDonationSelections {
  void impactDonation
  return mergeApplicationDonationSelection(impactDonation, selection)
}

export function needsDonationPrompt(
  impactDonation: ImpactDonationChoice | null | undefined,
  requiresDonation: boolean
): boolean {
  if (!requiresDonation) {
    return false
  }
  return !hasApplicationDonationSelection(impactDonation)
}

export function parseImpactDonationSession(
  value: string | null,
  fallbackAccountIds: string[] = []
): ImpactDonationChoice | null {
  if (!value) {
    return null
  }
  try {
    return normalizeImpactDonationChoice(JSON.parse(value), fallbackAccountIds)
  } catch {
    return null
  }
}

export function getDonationReturnPath(searchParams?: URLSearchParams): DonationReturnPath {
  const fromQuery = searchParams?.get('returnTo')?.trim()
  if (fromQuery === '/apply/express') {
    return '/apply/express'
  }
  if (typeof window !== 'undefined') {
    try {
      const stored = sessionStorage.getItem(DONATION_RETURN_TO_SESSION_KEY)
      if (stored === '/apply/express') {
        return '/apply/express'
      }
    } catch {
      // ignore storage errors
    }
  }
  return '/apply'
}

export function storeDonationReturnPath(path: DonationReturnPath): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    sessionStorage.setItem(DONATION_RETURN_TO_SESSION_KEY, path)
  } catch {
    // ignore storage errors
  }
}

export function buildDonationChoiceUrl(returnTo: DonationReturnPath): string {
  return `/apply/donation-choice?returnTo=${encodeURIComponent(returnTo)}`
}

export function readResumeImpactDonation(value: unknown): ImpactDonationChoice | null {
  return normalizeImpactDonationChoice(value)
}
